import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UserButton } from '@clerk/react';

import { useSessions, type Tab } from '../hooks/useSessions';
import { TabStrip } from './TabStrip';
import { ChatPane } from './ChatPane';
import { LeftSidebar } from './LeftSidebar';
import { RightSidebar } from './RightSidebar';
import { FileViewerPane } from './FileViewerPane';

/**
 * The signed-in workspace.
 *
 * Three-column layout: collapsible left sidebar (sessions list),
 * main pane area (header + tab strip + per-tab body), collapsible
 * right sidebar (workspace file explorer). Each open tab keeps its
 * pane mounted (with `hidden` + `inert` for inactive ones) so chat
 * sessions keep streaming in the background and file viewers preserve
 * their scroll position.
 *
 * `activeId` and `focusTab` come from `useSessions()` (lifted into
 * the shared context) so the tab strip here AND the sessions list in
 * `LeftSidebar` flip focus through the same urgent-priority state
 * update. Reading from a regular React state field — instead of
 * deriving it from `useSearchParams` — sidesteps react-router-dom
 * v7's `BrowserRouter` wrapping location updates in `startTransition`
 * (verified at `react-router/dist/development/chunk-EVOBXE3Y.mjs:10100`),
 * which left newly-created tabs visually inactive on the urgent
 * render after a click — the URL transition would eventually flush,
 * but for fast clicks it could be starved, leaving the wrong tab
 * focused.
 */
export function ChatWorkspace() {
  const { tabs, activeId, createTab, removeTab, renameTab, focusTab } =
    useSessions();

  const activeTab: Tab | null = useMemo(() => {
    if (!activeId) return null;
    return tabs.find((t) => t.id === activeId) ?? null;
  }, [tabs, activeId]);

  // Auto-create the first chat tab on a fresh session. Ref-gated so it
  // CANNOT fire more than once for the lifetime of this component.
  // Without the gate, React StrictMode's effect double-invocation +
  // batched setTabs updates would queue multiple creations against the
  // same captured `tabs=[]` snapshot, and any setSearchParams identity
  // change in deps could re-trigger the effect before the queued state
  // settled — both observed as a "Maximum update depth exceeded" loop.
  const didAutoCreateRef = useRef(false);
  useEffect(() => {
    if (didAutoCreateRef.current) return;
    if (tabs.length > 0) {
      didAutoCreateRef.current = true;
      return;
    }
    didAutoCreateRef.current = true;
    const t = createTab();
    focusTab(t.id, { replace: true });
  }, [tabs.length, createTab, focusTab]);

  const handleSelect = useCallback(
    (id: string) => {
      focusTab(id);
    },
    [focusTab],
  );

  const handleNew = useCallback(() => {
    const t = createTab();
    focusTab(t.id);
  }, [createTab, focusTab]);

  const handleClose = useCallback(
    (id: string) => {
      removeTab(id);
      // `useSessions`'s drift-correct effect picks a sibling on the
      // next render if `id` was the active tab.
    },
    [removeTab],
  );

  return (
    <div className="flex h-screen min-h-0">
      <LeftSidebar activeTabId={activeId} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-white/8 px-4 py-2">
          <div className="flex items-center gap-2 pe-2">
            <span aria-hidden="true" className="text-lg">
              🪶
            </span>
            <span className="font-semibold tracking-tight" translate="no">
              Bluebird
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <TabStrip
              tabs={tabs}
              activeId={activeId}
              onSelect={handleSelect}
              onClose={handleClose}
              onRename={renameTab}
              onNew={handleNew}
            />
          </div>
          <div className="ms-2">
            <UserButton />
          </div>
        </header>

        <main className="relative flex min-h-0 flex-1">
          {tabs.map((tab) => {
            const inactive = tab.id !== activeId;
            return (
              <div
                key={tab.id}
                className="absolute inset-0 flex min-h-0"
                hidden={inactive}
                aria-hidden={inactive}
                inert={inactive}
              >
                {tab.kind === 'chat' ? (
                  <ChatPane sessionId={tab.id} isActive={!inactive} />
                ) : (
                  <FileViewerPane tab={tab} isActive={!inactive} />
                )}
              </div>
            );
          })}
        </main>
      </div>
      <RightSidebar activeTab={activeTab} />
    </div>
  );
}
